import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGripVertical,
  faExpand,
  faCompress,
  faExternalLinkAlt,
  faWindowMinimize,
  faTriangleExclamation,
  faSpinner,
} from '@/fontawesome';
import { useDraggable } from '@/hooks/useDraggable';
import { DAY_TRADING_ROUTES } from '@/constants/routes';

type Status = 'connecting' | 'playing' | 'error';

interface DirectStreamPiPProps {
  viewerUrl: string;
  minimized: boolean;
  onMinimize: () => void;
}

const DEFAULT_POSITION = { right: 16, bottom: 16 };
const VIDEO_WIDTH_SMALL = 384;

export default function DirectStreamPiP({ viewerUrl, minimized, onMinimize }: DirectStreamPiPProps) {
  const [mode, setMode] = useState<'small' | 'expanded'>('small');
  const [status, setStatus] = useState<Status>('connecting');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const isActive = !minimized && viewerUrl !== '';

  const { position, setPosition, isDragging } = useDraggable({
    initialPosition: DEFAULT_POSITION,
    handleRef,
    elementRef,
    enabled: isActive,
  });

  useEffect(() => {
    if (minimized || viewerUrl === '') {
      return;
    }
    const video = videoRef.current;
    if (video === null) {
      return;
    }

    setStatus('connecting');
    setErrorMessage('');

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    if (Hls.isSupported()) {
      // Fatal network errors must fully tear down hls.js: LL-HLS uses blocking
      // long-poll playlist requests, and a tight retry loop will exhaust the
      // browser's 6-connection-per-origin budget and starve every other XHR.
      const scheduleRetry = () => {
        if (cancelled) return;
        if (hlsRef.current !== null) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        attempt += 1;
        const delay = Math.min(3000, 1000 * 2 ** (attempt - 1));
        setStatus('error');
        setErrorMessage(`Verbindung zum Livestream verloren –\nneuer Versuch in ${Math.round(delay / 1000)}s…`);
        retryTimer = setTimeout(() => {
          if (cancelled) return;
          startHls();
        }, delay);
      };

      const startHls = () => {
        if (cancelled) return;
        setStatus('connecting');
        setErrorMessage('');
        const hls = new Hls({
          lowLatencyMode: true,
          liveSyncDuration: 1.5,
          liveMaxLatencyDuration: 4,
          maxLiveSyncPlaybackRate: 1.5,
          backBufferLength: 4,
          maxBufferLength: 4,
        });
        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (cancelled) return;
          if (data.fatal !== true) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          scheduleRetry();
        });

        hls.loadSource(viewerUrl);
        hls.attachMedia(video);
      };

      startHls();

      const onPlaying = () => {
        attempt = 0;
        setStatus('playing');
      };
      const onWaiting = () => setStatus('connecting');
      const onCanPlay = () => {
        video.play().catch(() => {
          // Autoplay blocked — user will tap play manually.
        });
      };
      const onPause = () => {
        video.play().catch(() => {});
      };
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('playing', onPlaying);
      video.addEventListener('waiting', onWaiting);
      video.addEventListener('pause', onPause);

      return () => {
        cancelled = true;
        if (retryTimer !== null) {
          clearTimeout(retryTimer);
        }
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('pause', onPause);
        if (hlsRef.current !== null) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }

    // Safari plays HLS natively.
    video.src = viewerUrl;
    const onCanPlay = () => {
      video.play().catch(() => {
        // ignore — manual play required
      });
    };
    const onPlaying = () => setStatus('playing');
    const onWaiting = () => setStatus('connecting');
    const onError = () => {
      setStatus('error');
      setErrorMessage('Livestream konnte nicht abgespielt werden.');
    };
    const onPause = () => {
      video.play().catch(() => {});
    };
    video.addEventListener('canplay', onCanPlay, { once: true });
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('error', onError);
    video.addEventListener('pause', onPause);

    return () => {
      cancelled = true;
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('error', onError);
      video.removeEventListener('pause', onPause);
      video.removeAttribute('src');
      video.load();
    };
  }, [viewerUrl, minimized]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const noop = () => {};
    const actions: MediaSessionAction[] = ['pause', 'stop', 'seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack'];
    actions.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, noop);
      } catch {
        // unsupported action — ignore
      }
    });
    return () => {
      actions.forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // unsupported action — ignore
        }
      });
    };
  }, []);

  if (minimized || viewerUrl === '') {
    return null;
  }

  const handleExpand = () => {
    setMode((prev) => (prev === 'small' ? 'expanded' : 'small'));
    setPosition(DEFAULT_POSITION);
  };

  const handlePopOut = () => {
    const popoutUrl = `${DAY_TRADING_ROUTES.LIVESTREAM}?url=${encodeURIComponent(viewerUrl)}`;
    window.open(popoutUrl, 'livestream', 'width=800,height=600,menubar=no,toolbar=no');
    onMinimize();
  };

  const isExpanded = mode === 'expanded';

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    right: position.right,
    bottom: position.bottom,
    zIndex: 40,
  };

  if (isExpanded) {
    containerStyle.height = 'min(50vh, calc(100vh - 32px))';
    containerStyle.width = `calc((min(50vh, calc(100vh - 32px)) - 36px) * 16 / 9)`;
  } else {
    containerStyle.width = VIDEO_WIDTH_SMALL;
  }

  return (
    <div ref={elementRef} style={containerStyle} className="rounded-lg shadow-2xl overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
      <div
        ref={handleRef}
        className="h-9 flex items-center gap-2 px-3 bg-gray-100 dark:bg-gray-900 cursor-move select-none shrink-0"
      >
        <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 text-gray-400 dark:text-gray-500" />
        <span className="text-gray-800 dark:text-white text-xs font-medium truncate flex-1">Stream</span>

        <button
          onClick={handleExpand}
          title={isExpanded ? 'Verkleinern' : 'Vergrößern'}
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} className="w-3.5 h-3.5" />
        </button>

        <button
          onClick={handlePopOut}
          title="Im neuen Fenster öffnen"
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
        </button>

        <button
          onClick={onMinimize}
          title="Minimieren"
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={faWindowMinimize} className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        <div
          className="relative bg-black"
          style={isExpanded ? { flex: 1, minWidth: 0 } : { width: VIDEO_WIDTH_SMALL, aspectRatio: '16/9' }}
        >
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full"
            playsInline
            autoPlay
            controls
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload noplaybackrate"
          />
          {status === 'connecting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white text-xs pointer-events-none">
              <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
              <span>Stream wird geladen…</span>
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 text-white text-xs text-center px-4 pointer-events-none">
              <FontAwesomeIcon icon={faTriangleExclamation} className="w-5 h-5 text-amber-400" />
              <span className="whitespace-pre-line">{errorMessage !== '' ? errorMessage : 'Livestream nicht verfügbar.'}</span>
            </div>
          )}
          {isDragging && <div className="absolute inset-0 z-10" />}
        </div>
      </div>
    </div>
  );
}
